import type { PlatformMessagingNotice } from '@exylon/generated-contract-kernel/types';
import { addCalendarDays, formatMadridInstant, madridClock, madridDay } from '../calendar';
import { formatCount } from '../count';
import { isCanonicalUuid } from '../../runtime/format';

export type NoticeRecipientState = 'new' | 'seen' | 'read' | 'dismissed';

/** backend/src/platform/messaging/schema.ts — record_notice_receipt accumulates timestamps. */
export function noticeRecipientState(notice: PlatformMessagingNotice): NoticeRecipientState {
  return notice.dismissedAt ? 'dismissed' : notice.readAt ? 'read' : notice.seenAt ? 'seen' : 'new';
}

/** The producer's `dismiss_policy` is the only dismiss rule (backend `record_notice_receipt`); severity forbids nothing. */
export const noticeDismissible = (notice: Pick<PlatformMessagingNotice, 'dismissPolicy' | 'dismissedAt'>): boolean =>
  notice.dismissPolicy !== 'required' && notice.dismissedAt === null;

export function noticeDismissReason(notice: PlatformMessagingNotice): string | null {
  return notice.dismissPolicy === 'required' ? 'Este aviso requiere lectura; no se puede descartar.' : null;
}

/** The state of your copy, in a word (canonical monotonic receipts): a notice seen is still counted unread. */
export function noticeStateLine(notice: PlatformMessagingNotice): string {
  switch (noticeRecipientState(notice)) {
    case 'new': return 'Sin leer';
    case 'seen': return 'Visto · sin leer';
    case 'read': return 'Leído';
    case 'dismissed': return 'Descartado';
  }
}

/** `unreadCount` is the Backend's exact total of the whole inbox, never a count of the notices read. */
export function noticeInboxHeading(items: readonly PlatformMessagingNotice[], complete: boolean, unreadCount: number): string {
  if (unreadCount > 0) return (formatCount(unreadCount) ?? String(unreadCount)) + ' sin leer';
  return complete && !items.length ? 'ninguno' : 'todos leídos';
}

export function noticeTime(createdAt: string, now: number): string {
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created) || !Number.isFinite(now)) return 'Fecha · sin datos';
  try {
    const clock = madridClock(created);
    const age = now - created;
    if (age >= 0 && age < 60_000) return 'ahora';
    if (age >= 60_000 && age < 3_600_000) return 'hace ' + String(Math.floor(age / 60_000)) + ' min';
    const today = madridDay(now);
    if (madridDay(created) === today) return 'hoy ' + clock;
    if (madridDay(created) === addCalendarDays(today, -1)) return 'ayer ' + clock;
    return formatMadridInstant(created, { day: 'numeric', month: 'short' }) + ' · ' + clock;
  } catch { return 'Fecha · sin datos'; }
}

/** One inbox row: the newest notice of a run of repeated ones, and every copy it stands for (newest first). */
interface NoticeGroup {
  readonly lead: PlatformMessagingNotice;
  readonly members: readonly PlatformMessagingNotice[];
}

/** The notice's kind: its category without the identifiers a producer appends (`service.coverage.manager.<item>.<n>`). */
function noticeKind(category: string): string {
  return category.split('.').filter((segment) => !isCanonicalUuid(segment) && !/^\d+$/.test(segment)).join('.');
}

/**
 * Repeated notices read as one row with a count: the same kind, the same text,
 * the same severity and dismiss policy, and the same state of your copy. The
 * row sits where its newest copy is; its actions apply to every copy.
 */
export function groupNotices(items: readonly PlatformMessagingNotice[]): readonly NoticeGroup[] {
  const groups = new Map<string, PlatformMessagingNotice[]>();
  for (const notice of items) {
    const key = JSON.stringify([noticeKind(notice.category), notice.title, notice.body, notice.severity,
      notice.dismissPolicy, noticeRecipientState(notice)]);
    const members = groups.get(key);
    if (members) members.push(notice);
    else groups.set(key, [notice]);
  }
  return [...groups.values()].map((members) => ({ lead: members[0]!, members }));
}
